import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/apiService';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { UserContext } from '../../contexts/UserContext';
import { UserForm, UserFormNotice } from './UserForm';
import { formatBackendErrors } from '../../utils/errorUtils';
import type { User } from '../../types/types';

function resolveUserPhotoUrl(photo?: string | null): string | undefined {
  if (!photo?.trim()) return undefined;
  const p = photo.trim();
  if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('data:')) return p;
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '') || '';
  const path = p.startsWith('/') ? p : `/${p}`;
  if (!base) return path;
  return `${base}${path}`;
}

type PanelMode = 'create' | 'view' | 'edit';

export const UsersList = () => {
  const { user } = useContext(UserContext);
  const canRead = Boolean(user?.permissions?.users?.read);
  const canCreate = Boolean(user?.permissions?.users?.create);

  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(canRead);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [notice, setNotice] = useState<UserFormNotice | null>(null);
  /** Row whose invite is being resent: a second click would revoke the link just sent. */
  const [resendingId, setResendingId] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>('view');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  /** Set by UserForm for view/edit drawer title: "{First} {Last} - profile details" */
  const [drawerProfileTitle, setDrawerProfileTitle] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!canRead) return;
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.get('/api/users');
      setUsers(res);
    } catch {
      setError('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    if (canRead) {
      void fetchUsers();
    } else {
      setIsLoading(false);
    }
  }, [canRead, fetchUsers]);

  const openDrawer = (mode: PanelMode, userId: string | null) => {
    setPanelMode(mode);
    setSelectedUserId(userId);
    setDrawerProfileTitle(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedUserId(null);
    setDrawerProfileTitle(null);
  };

  const handleViewUser = (id: string) => openDrawer('view', id);
  const handleEditUser = (id: string) => openDrawer('edit', id);

  const handleDeleteUser = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/api/users/${id}`);
        setUsers((prev) => prev.filter((u) => u.id !== id));
      } catch {
        alert('Failed to delete user');
      }
    }
  };

  const handleResendInvite = async (row: User) => {
    if (resendingId) return;
    if (!window.confirm(`Send a new invitation email to ${row.username}? It lets them set a new password, and any earlier invitation link will stop working.`)) {
      return;
    }
    setResendingId(row.id);
    try {
      const res: { invite_sent?: string } | undefined = await api.post(`/api/users/${row.id}/invite`, {
        base_url: window.location.origin,
      });
      if (res?.invite_sent === 'queued') {
        setNotice({ severity: 'success', text: `A new invitation email is on its way to ${row.username}.` });
      } else if (res?.invite_sent === 'skipped') {
        setNotice({ severity: 'info', text: `No invitation was sent to ${row.username}. They can still sign in after using "Forgot Password?" on the login page.` });
      } else {
        setNotice({ severity: 'warning', text: `Couldn't send the invitation to ${row.username}. Please try again.` });
      }
    } catch (err: any) {
      const reason = err?.status === 400 ? ` ${formatBackendErrors(err)}` : ' Please try again.';
      setNotice({ severity: 'warning', text: `Couldn't send the invitation to ${row.username}.${reason}` });
    } finally {
      setResendingId(null);
    }
  };

  const drawerTitle = useMemo(() => {
    if (panelMode === 'create') return 'Create user';
    return drawerProfileTitle ?? 'Profile details';
  }, [panelMode, drawerProfileTitle]);

  const formKey = `${panelMode}-${selectedUserId ?? 'new'}`;

  if (canRead && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (canRead && error) {
    return (
      <Box sx={{ color: '#f87171', textAlign: 'center', py: 4 }}>
        {error}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {deleteError && (
        <Alert
          severity="error"
          onClose={() => setDeleteError(null)}
          sx={{
            mb: 2,
            bgcolor: '#27272a',
            color: '#f87171',
            border: '1px solid #3f3f46',
          }}
        >
          <AlertTitle sx={{ color: '#f87171' }}>Error</AlertTitle>
          {deleteError}
        </Alert>
      )}

      {notice && (
        <Alert
          severity={notice.severity}
          onClose={() => setNotice(null)}
          sx={{ mb: 2, bgcolor: '#18181b', color: '#e4e4e7', border: '1px solid #3f3f46' }}
        >
          {notice.text}
        </Alert>
      )}

      {canRead && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            bgcolor: '#111111',
            border: '1px solid #27272a',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table size="medium">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#141414',
                  '& th': { borderColor: '#27272a', py: 1.75 },
                }}
              >
                <TableCell sx={{ width: 72, borderColor: '#27272a' }} />
                <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                  FIRST NAME
                </TableCell>
                <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                  LAST NAME
                </TableCell>
                <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                  EMAIL
                </TableCell>
                <TableCell align="right" sx={{ width: 160, borderColor: '#27272a' }}>
                  <Tooltip title="Refresh list">
                    <IconButton
                      onClick={() => void fetchUsers()}
                      aria-label="refresh"
                      size="small"
                      sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ borderColor: '#27272a', py: 5, textAlign: 'center', color: '#71717a' }}>
                    No users yet. {canCreate ? 'Use + to add the first user.' : ''}
                  </TableCell>
                </TableRow>
              )}
              {users.map((row) => {
                const photo = resolveUserPhotoUrl(row.profile_picture);
                const initials = `${row.first_name?.[0] ?? ''}${row.last_name?.[0] ?? ''}`.toUpperCase() || '?';
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      '&:last-of-type td': { borderBottom: 0 },
                      '& td': { borderColor: '#27272a', py: 1.5 },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell sx={{ borderColor: '#27272a' }}>
                      <Avatar
                        src={photo}
                        alt=""
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: '#27272a',
                          color: '#e4e4e7',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          border: '1px solid #3f3f46',
                        }}
                      >
                        {initials}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ color: '#fafafa', fontWeight: 500, borderColor: '#27272a' }}>
                      {row.first_name}
                    </TableCell>
                    <TableCell sx={{ color: '#fafafa', fontWeight: 500, borderColor: '#27272a' }}>
                      {row.last_name}
                    </TableCell>
                    <TableCell sx={{ color: '#d4d4d8', borderColor: '#27272a' }}>{row.username}</TableCell>
                    <TableCell align="right" sx={{ borderColor: '#27272a' }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.25}>
                        {user?.permissions?.users?.read && (
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => handleViewUser(row.id)}
                              size="small"
                              sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCreate && row.id !== user?.id && (
                          <Tooltip title="Resend invite">
                            {/* span: a Tooltip needs an enabled child to listen to while the button is disabled */}
                            <span>
                            <IconButton
                              onClick={() => void handleResendInvite(row)}
                              disabled={resendingId !== null}
                              size="small"
                              aria-label={`resend invite to ${row.username}`}
                              sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                            >
                              <ForwardToInboxIcon fontSize="small" />
                            </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {user?.permissions?.users?.update && (
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditUser(row.id)}
                              size="small"
                              sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user?.permissions?.users?.delete && (
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => void handleDeleteUser(row.id)}
                              size="small"
                              sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!canRead && canCreate && (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: 'center',
            border: '1px dashed #3f3f46',
            borderRadius: 2,
            color: '#a1a1aa',
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            You do not have access to the user directory.
          </Typography>
          <Typography variant="body2" sx={{ color: '#71717a' }}>
            Use the button below to create a new user.
          </Typography>
        </Box>
      )}

      {canCreate && (
        <Fab
          color="primary"
          aria-label="create user"
          onClick={() => openDrawer('create', null)}
          sx={{
            position: 'fixed',
            right: { xs: 20, sm: 28 },
            bottom: { xs: 20, sm: 28 },
            zIndex: (theme) => theme.zIndex.drawer - 1,
            bgcolor: '#60a5fa',
            color: '#0a0a0a',
            boxShadow: '0 8px 24px rgba(96, 165, 250, 0.35)',
            '&:hover': { bgcolor: '#3b82f6' },
          }}
        >
          <PersonAddIcon />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '50vw' },
            maxWidth: { xs: '100%', sm: '50vw' },
            minWidth: { sm: 360 },
            bgcolor: '#0a0a0a',
            borderLeft: '1px solid #27272a',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: 2,
            minHeight: 56,
            borderBottom: '1px solid #27272a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600, flex: 1, minWidth: 0 }}>
            {drawerTitle}
          </Typography>
          <IconButton
            edge="end"
            onClick={closeDrawer}
            aria-label="close panel"
            sx={{ color: '#a1a1aa', '&:hover': { color: '#fafafa' } }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {drawerOpen && (panelMode === 'create' || selectedUserId) && (
            <UserForm
              key={formKey}
              mode={panelMode}
              userId={selectedUserId ?? undefined}
              embedded
              onClose={closeDrawer}
              onSuccess={(n) => {
                if (n) setNotice(n);
                void fetchUsers();
              }}
              onHeaderDisplayNameChange={setDrawerProfileTitle}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};
